import { type JSX, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/icons';
import { CUSTOMER_FAQS_EN } from '@/content/help/customer.en';
import { CUSTOMER_FAQS_ES } from '@/content/help/customer.es';
import { PLATFORM_FAQS_EN } from '@/content/help/platform.en';
import { PLATFORM_FAQS_ES } from '@/content/help/platform.es';
import { RESELLER_FAQS_EN } from '@/content/help/reseller.en';
import { RESELLER_FAQS_ES } from '@/content/help/reseller.es';
import { CATEGORIES, type FAQ, type HelpCategory } from '@/content/help/types';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

import { FAQItem } from './faq-item';

interface HelpContentProps {
  /** "page" gets the full sidebar; "drawer" hides it (search-driven). */
  layout: 'page' | 'drawer';
}

/**
 * Shared FAQ surface — used both at /help and inside the drawer.
 *
 * Search runs over question + answer text + tags, lowercase
 * substring match. Cheap; the FAQ corpus is < 100 entries today.
 * If we ever exceed a few hundred, swap in fuse.js or wire up the
 * backend (DB-backed FAQs would also enable per-tenant overrides).
 */
export function HelpContent({ layout }: HelpContentProps): JSX.Element {
  const { t, i18n } = useTranslation('default');
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<HelpCategory | 'all'>('all');

  // Pick the FAQ corpus by current language. Each file shares the same
  // `id` slugs across es/en so deep links (#login, etc) resolve in
  // either bundle. Default to ES — founder's primary language + app
  // default.
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'es').slice(0, 2);
  const tenantType = user?.tenantType ?? '';

  // Audience-aware merge: customer FAQs are universal; reseller-only
  // entries layer in for reseller + platform tenants; platform-only
  // entries layer in for platform tenants. Resellers and platform
  // admins still need the customer-facing entries because they often
  // operate on behalf of customers (impersonation, support).
  const faqs = useMemo(() => {
    const customer = lang === 'en' ? CUSTOMER_FAQS_EN : CUSTOMER_FAQS_ES;
    const reseller = lang === 'en' ? RESELLER_FAQS_EN : RESELLER_FAQS_ES;
    const platform = lang === 'en' ? PLATFORM_FAQS_EN : PLATFORM_FAQS_ES;
    const merged = [...customer];
    if (tenantType === 'reseller' || tenantType === 'platform') merged.push(...reseller);
    if (tenantType === 'platform') merged.push(...platform);
    return merged;
  }, [lang, tenantType]);

  const filtered = useMemo(
    () => filterFAQs(faqs, query, activeCategory),
    [faqs, query, activeCategory],
  );

  const showSidebar = layout === 'page';
  const isSearching = query.trim().length > 0;
  const autoExpand = filtered.length === 1;

  const counts = useMemo(() => countByCategory(faqs), [faqs]);

  return (
    <div className={cn('flex gap-6', !showSidebar && 'flex-col')}>
      {/* Sidebar (page only) */}
      {showSidebar && (
        <aside className="w-[200px] flex-shrink-0">
          <CategorySidebar
            active={activeCategory}
            counts={counts}
            onPick={(c) => setActiveCategory(c)}
          />
        </aside>
      )}

      {/* Main column */}
      <div className="flex-1 min-w-0">
        <div className="mb-4">
          <div className="relative">
            <Icon.search
              width="16"
              height="16"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-text-3 pointer-events-none"
            />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('help_search_placeholder')}
              autoFocus={layout === 'drawer'}
              className="w-full h-10 pl-9 pr-3 text-[13.5px] rounded-md border border-line-strong bg-surface text-text placeholder:text-text-4 focus:border-volt-600 focus:outline-none focus:ring-2 focus:ring-volt-glow"
            />
          </div>
          {isSearching && (
            <p className="text-[12px] text-text-3 mt-2">
              {t('help_search_count', { count: filtered.length })}
            </p>
          )}
        </div>

        {filtered.length === 0 ? (
          <div className="py-10 text-center text-[13px] text-text-3">
            {t('help_no_results')}
          </div>
        ) : (
          <div>
            {/* When NOT searching + on page layout, group by category. */}
            {!isSearching && showSidebar && activeCategory === 'all' ? (
              <CategoryGrouped faqs={filtered} />
            ) : (
              <div>
                {filtered.map((f) => (
                  <FAQItem
                    key={f.id}
                    faq={f}
                    defaultOpen={autoExpand}
                    highlight={isSearching ? query : undefined}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sidebar
// ---------------------------------------------------------------------------
function CategorySidebar({
  active,
  counts,
  onPick,
}: {
  active: HelpCategory | 'all';
  counts: Record<HelpCategory, number>;
  onPick: (c: HelpCategory | 'all') => void;
}): JSX.Element {
  const { t } = useTranslation('default');
  const total = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <nav className="text-[13px]">
      <SidebarBtn
        active={active === 'all'}
        label={t('help_cat_all')}
        count={total}
        onClick={() => onPick('all')}
      />
      <div className="mt-2 space-y-0.5">
        {CATEGORIES.filter((c) => counts[c.id] > 0).map((c) => (
          <SidebarBtn
            key={c.id}
            active={active === c.id}
            label={t(c.labelKey)}
            count={counts[c.id]}
            onClick={() => onPick(c.id)}
          />
        ))}
      </div>
    </nav>
  );
}

function SidebarBtn({
  active,
  label,
  count,
  onClick,
}: {
  active: boolean;
  label: string;
  count: number;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md text-left transition-colors',
        active
          ? 'bg-surface-3 text-text font-medium'
          : 'text-text-2 hover:bg-surface-2 hover:text-text',
      )}
    >
      <span className="truncate">{label}</span>
      <span className="text-[11px] text-text-4 tabular-nums">{count}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Grouped layout (page, no search, "all" category)
// ---------------------------------------------------------------------------
function CategoryGrouped({ faqs }: { faqs: FAQ[] }): JSX.Element {
  const { t } = useTranslation('default');
  const grouped = CATEGORIES.map((c) => ({
    cat: c,
    items: faqs.filter((f) => f.category === c.id),
  })).filter((g) => g.items.length > 0);

  return (
    <div className="space-y-8">
      {grouped.map(({ cat, items }) => (
        <section key={cat.id}>
          <h2 className="display text-[13px] uppercase tracking-[0.06em] text-text-4 font-semibold mb-2">
            {t(cat.labelKey)}
          </h2>
          <div>
            {items.map((f) => (
              <FAQItem key={f.id} faq={f} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Search / filter helpers
// ---------------------------------------------------------------------------
function filterFAQs(
  faqs: FAQ[],
  query: string,
  category: HelpCategory | 'all',
): FAQ[] {
  const q = query.trim().toLowerCase();
  return faqs.filter((f) => {
    if (category !== 'all' && f.category !== category) return false;
    if (!q) return true;
    if (f.question.toLowerCase().includes(q)) return true;
    if (f.tags?.some((t) => t.toLowerCase().includes(q))) return true;
    // Last resort: serialize the answer to text for substring search.
    // Not perfect for JSX (loses tags), but covers the common case.
    if (renderableText(f.answer).toLowerCase().includes(q)) return true;
    return false;
  });
}

function countByCategory(faqs: FAQ[]): Record<HelpCategory, number> {
  return faqs.reduce<Record<HelpCategory, number>>(
    (acc, f) => {
      acc[f.category] = (acc[f.category] ?? 0) + 1;
      return acc;
    },
    {
      getting_started: 0,
      senders: 0,
      contacts: 0,
      sending: 0,
      voice: 0,
      ai_agents: 0,
      inbound: 0,
      compliance: 0,
      billing: 0,
      reports: 0,
      troubleshooting: 0,
      customers: 0,
      branding: 0,
      platform: 0,
    },
  );
}

/**
 * Best-effort string extraction from a ReactNode. Used only for search
 * — accuracy matters less than catching obvious matches like "TCPA" or
 * "10DLC" mentioned inside the answer body.
 */
function renderableText(node: unknown): string {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(renderableText).join(' ');
  if (node && typeof node === 'object' && 'props' in node) {
    const props = (node as { props: { children?: unknown } }).props;
    return renderableText(props.children);
  }
  return '';
}
